import { visit } from 'unist-util-visit'

// Docusaurus admonitions (`:::warning[Recommended]`) rendered without Docusaurus.
//
// `remark-directive` does the parsing; this maps the resulting container
// directive onto plain elements react-markdown can render. There are 34 of these
// across the docs, so leaving them as literal `:::` text was never an option.
//
// Kinds match what the content actually uses. `caution` is Docusaurus 2 spelling
// that still appears in three pages; it shares the `warning` tone rather than
// getting its own, which is also what Docusaurus 3 does with it.

export type AdmonitionKind =
  | 'note'
  | 'tip'
  | 'info'
  | 'caution'
  | 'warning'
  | 'danger'

interface AdmonitionStyle {
  tone: 'neutral' | 'success' | 'info' | 'warning' | 'danger'
  defaultTitle: string
}

const ADMONITIONS: Record<AdmonitionKind, AdmonitionStyle> = {
  note: { tone: 'neutral', defaultTitle: 'Note' },
  tip: { tone: 'success', defaultTitle: 'Tip' },
  info: { tone: 'info', defaultTitle: 'Info' },
  caution: { tone: 'warning', defaultTitle: 'Caution' },
  warning: { tone: 'warning', defaultTitle: 'Warning' },
  danger: { tone: 'danger', defaultTitle: 'Danger' },
}

const isAdmonitionKind = (name: string): name is AdmonitionKind =>
  Object.prototype.hasOwnProperty.call(ADMONITIONS, name)

// Structural subset of the mdast nodes this touches — enough to be type-safe
// without depending on @types/mdast, which is not in the workspace. Exported
// because directives.tsx walks the same nodes.
export interface DirectiveNode {
  type: string
  name?: string
  /** `::name{key="value"}` attributes, parsed by remark-directive. */
  attributes?: Record<string, string | null | undefined>
  children?: DirectiveChild[]
  data?: {
    hName?: string
    hProperties?: Record<string, unknown>
    directiveLabel?: boolean
  }
}

interface DirectiveChild extends DirectiveNode {
  value?: string
}

const titleParagraph = (title: string): DirectiveChild => ({
  type: 'paragraph',
  data: {
    hName: 'p',
    hProperties: { className: ['curri-admonition__title'] },
  },
  children: [{ type: 'text', value: title }],
})

export const remarkAdmonitions = () => (tree: DirectiveNode) => {
  visit(tree, 'containerDirective', (node: DirectiveNode) => {
    const name = node.name ?? ''
    // An unrecognised `:::something` is left as-is rather than styled as a
    // generic box — silently rendering an unknown directive as a note is how a
    // typo becomes invisible.
    if (!isAdmonitionKind(name)) return

    const style = ADMONITIONS[name]
    const children = node.children ?? []

    // remark-directive turns `[Title]` into a first child flagged as the label.
    const label = children.find(child => child.data?.directiveLabel)

    if (label) {
      label.data = {
        ...label.data,
        hName: 'p',
        hProperties: { className: ['curri-admonition__title'] },
      }
    } else {
      node.children = [titleParagraph(style.defaultTitle), ...children]
    }

    node.data = {
      ...node.data,
      hName: 'aside',
      hProperties: {
        className: ['curri-admonition', `curri-admonition--${style.tone}`],
        // Read by assistive tech as a labelled aside rather than an unnamed
        // region, which is what a bare <aside> full of prose would be.
        'aria-label': label ? undefined : style.defaultTitle,
      },
    }
  })
}
